/**
 * A command that can be taken back and done again.
 *
 * `label` names what it did, as a string key, so the app can say "Undone:
 * Clear map" rather than leave the user guessing which of forty steps went.
 */
export interface Command {
  label?: string;

  undo(): Promise<any> | any;

  redo(): Promise<any> | any;
}

/**
 * An undo history: a list of commands and a cursor into it.
 *
 * Commands before the cursor can be undone, the ones after it redone, and
 * adding a command drops everything after the cursor, as every editor does.
 *
 * What changed from the first version, and why:
 *  - undo/redo wait for the command. The map's commands write to Firestore and
 *    a restore talks to the server; returning before they finish let a second
 *    Ctrl+Z start while the first was half applied.
 *  - `executing` is reset in a finally. It used to be set and cleared around a
 *    synchronous call, so one exception left it stuck and every later `add`
 *    was silently ignored for the rest of the session.
 *  - The history is capped. It grew forever, with two copies of every shape
 *    touched by every step.
 *  - Listeners are told about every change, so the Undo and Redo buttons can be
 *    disabled when there is nothing to do; they never were.
 */
export class UndoManager<C extends Command = Command> {

  private commands: C[] = [];
  private index = -1;
  private executing = false;
  private listeners: Array<() => void> = [];

  /** @param limit the most steps kept; the oldest go first. 0 keeps everything. */
  constructor(private readonly limit: number = 100) {
  }

  /** True while a command is being undone or redone. */
  get isExecuting(): boolean {
    return this.executing;
  }

  /**
   * Adds a command that has already been carried out.
   *
   * Ignored while a command is replaying itself: what an undo does is not a new
   * step.
   */
  add(command: C): this {
    if (this.executing) {
      return this;
    }
    this.commands.splice(this.index + 1);
    this.commands.push(command);
    if (this.limit > 0 && this.commands.length > this.limit) {
      this.commands.splice(0, this.commands.length - this.limit);
    }
    this.index = this.commands.length - 1;
    this.notify();
    return this;
  }

  /**
   * Undoes the newest command and returns it, or null when there is none.
   *
   * The cursor moves before the command runs: a command that fails half way is
   * not offered again, which would only fail again.
   */
  async undo(): Promise<C | null> {
    const command = this.commands[this.index];
    if (!command || this.executing) {
      return null;
    }
    this.executing = true;
    this.index -= 1;
    try {
      await command.undo();
    } finally {
      this.executing = false;
      this.notify();
    }
    return command;
  }

  /** Redoes the next command and returns it, or null when there is none. */
  async redo(): Promise<C | null> {
    const command = this.commands[this.index + 1];
    if (!command || this.executing) {
      return null;
    }
    this.executing = true;
    this.index += 1;
    try {
      await command.redo();
    } finally {
      this.executing = false;
      this.notify();
    }
    return command;
  }

  /** The command Undo would take back. */
  peekUndo(): C | null {
    return this.commands[this.index] || null;
  }

  /** The command Redo would do again. */
  peekRedo(): C | null {
    return this.commands[this.index + 1] || null;
  }

  /** Forgets every command. */
  clear() {
    const had = this.commands.length > 0;
    this.commands = [];
    this.index = -1;
    if (had) {
      this.notify();
    }
  }

  /**
   * Keeps only `command`, as either the step to undo (`undone` false) or the
   * step to redo. For commands that reload the whole map, after which every
   * other step points at shapes that no longer exist.
   */
  retainOnly(command: C, undone: boolean) {
    this.commands = [command];
    this.index = undone ? -1 : 0;
    this.notify();
  }

  hasUndo(): boolean {
    return this.index >= 0;
  }

  hasRedo(): boolean {
    return this.index < this.commands.length - 1;
  }

  /** Calls `listener` whenever the history changes. Returns the way to stop. */
  onChange(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => this.listeners = this.listeners.filter(l => l !== listener);
  }

  private notify() {
    this.listeners.forEach(listener => listener());
  }
}
